import { useMemo, useRef, useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useWizardFlow } from "../../hooks/useWizardFlow";
import { StepHeader } from "../../components/common/StepHeader";
import { EstimateCard } from "../../components/common/EstimateCard";
import { AdditionalDetails } from "../../components/wizard/AdditionalDetails";
import { CustomServiceRequest } from "../../components/wizard/CustomServiceRequest";
import { RadioQuestion } from "../../components/wizard/RadioQuestion";
import { CheckboxQuestion } from "../../components/wizard/CheckboxQuestion";
import { CollapsibleRoomDetails } from "../../components/wizard/CollapsibleRoomDetails";
import { PRICE_TRUST_LINE, sanitizeDisplayText } from "../../utils/priceUtils";
import { PAINTING_QUESTIONS } from "./paintingData";
import { calculatePaintingEstimate } from "./paintingPricing";
import { isPaintingQuestionVisible } from "./paintingValidation";

/**
 * Modular Painting flow — coordinator over the shared useWizardFlow hook.
 * Preserves the legacy question sequence, conditional logic, and pricing exactly.
 */
export const PaintingFlow = ({
  answers,
  setAnswers,
  additionalDetails,
  setAdditionalDetails,
  unlistedServiceDescription = '',
  setUnlistedServiceDescription = () => {},
  homeSqftValue = 1500,
  onComplete,
  onBack
}) => {
  const [expandedRooms, setExpandedRooms] = useState([]);
  const completeTimerRef = useRef(null);

  const estimate = useMemo(
    () => calculatePaintingEstimate(answers, homeSqftValue),
    [answers, homeSqftValue]
  );

  const handleComplete = (finalAnswers = null) => {
    const base = finalAnswers || answers;
    const withDetails = {
      ...base,
      ...(additionalDetails.trim() && { additional_details: additionalDetails.trim() })
    };
    onComplete(withDetails, calculatePaintingEstimate(base, homeSqftValue));
  };

  const flow = useWizardFlow({
    questions: PAINTING_QUESTIONS,
    answers,
    setAnswers,
    onComplete: () => handleComplete(),
    onBack,
    shouldShowQuestion: isPaintingQuestionVisible,
    autoAdvanceOnRadio: true,
    autoAdvanceDelay: 300
  });

  useEffect(() => () => clearTimeout(completeTimerRef.current), []);

  if (!flow.currentQuestion) return null;
  const q = flow.currentQuestion;

  const handleRadioSelect = (value) => {
    flow.handleRadioAnswer(value);
    // Last question (timeline) completes the flow after the same 300ms delay as legacy
    if (flow.isLastQuestion) {
      clearTimeout(completeTimerRef.current);
      completeTimerRef.current = setTimeout(() => {
        handleComplete({ ...answers, [q.id]: value });
      }, 300);
    }
  };

  const roomDetails = answers.room_details || {};

  return (
    <div className="animate-fade-in space-y-6" data-testid="painting-flow">
      <StepHeader
        title={sanitizeDisplayText(q.question)}
        subtitle={PRICE_TRUST_LINE}
        onBack={flow.goBack}
        showBack={true}
      />

      {q.type === "radio" && (
        <RadioQuestion
          question={q}
          value={answers[q.id]}
          onChange={handleRadioSelect}
        />
      )}

      {q.type === "checkbox" && (
        <>
          <CheckboxQuestion
            question={q}
            selectedValues={answers[q.id] || []}
            onChange={(values) => flow.setAnswer(q.id, values)}
          />
          <Button
            onClick={flow.goNext}
            disabled={!(answers[q.id] || []).length}
            className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl disabled:opacity-50"
            data-testid="checkbox-next"
          >
            Continue <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </>
      )}

      {q.type === "collapsible_room_details" && (
        <>
          <CollapsibleRoomDetails
            selectedRooms={answers.selected_rooms || []}
            roomConfig={q.roomConfig}
            surfaces={q.surfaces}
            sizeOptions={q.sizeOptions}
            drywallOptions={q.drywallOptions}
            roomDetails={roomDetails}
            expandedRooms={expandedRooms}
            onRoomToggle={(roomValue) =>
              setExpandedRooms((prev) =>
                prev.includes(roomValue)
                  ? prev.filter((r) => r !== roomValue)
                  : [...prev, roomValue]
              )
            }
            onRoomDetailChange={(roomValue, data) =>
              flow.setAnswer("room_details", { ...roomDetails, [roomValue]: data })
            }
          />
          <Button
            onClick={flow.goNext}
            className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl"
            data-testid="room-details-next"
          >
            Continue <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </>
      )}

      <AdditionalDetails
        value={additionalDetails}
        onChange={setAdditionalDetails}
        placeholder="Share any specific requirements, measurements, preferences, or unique project details that aren't covered above..."
      />

      {/* "Can't find what you need?" — show only on the first question */}
      {flow.currentQuestionIndex === 0 && (
        <CustomServiceRequest
          value={unlistedServiceDescription}
          onChange={setUnlistedServiceDescription}
        />
      )}

      <EstimateCard priceRange={estimate} isCustomRequest={!(estimate && (estimate.low > 0 || estimate.high > 0)) && !!unlistedServiceDescription.trim()} />
    </div>
  );
};

export default PaintingFlow;
